/**
 * Structured error format from the backend extraction service.
 */
interface ExtractionError {
  error_code: string;
  message: string;
  suggestion: string;
}

/**
 * Attempts to parse a JSON-encoded structured error from the backend.
 * Returns null if the string is not a valid structured error.
 */
function parseExtractionError(
  errorMessage: string | null | undefined,
): ExtractionError | null {
  if (!errorMessage) return null;
  try {
    const parsed = JSON.parse(errorMessage);
    if (parsed && typeof parsed.error_code === "string" && typeof parsed.message === "string") {
      return parsed as ExtractionError;
    }
  } catch {
    // Not a JSON error - legacy plain text
  }
  return null;
}

/**
 * Returns a user-friendly error message from a raw error_message field.
 * If the error is a structured JSON error, uses the i18n translation.
 * Falls back to a generic message for legacy plain-text errors.
 *
 * @param errorMessage - Raw error_message from the API
 * @param t - i18n translation function
 */
export function getExtractionErrorMessage(
  errorMessage: string | null | undefined,
  t: (key: string) => string,
): { message: string; suggestion: string } {
  const parsed = parseExtractionError(errorMessage);
  if (parsed) {
    const message = t(`extraction.errors.${parsed.error_code}.message`);
    const suggestion = t(`extraction.errors.${parsed.error_code}.suggestion`);
    return { message, suggestion };
  }
  // Legacy unstructured error - surface the raw backend message so callers
  // can see what actually failed; fall back to the generic message only when
  // the backend gave us nothing.
  const trimmed = errorMessage?.trim();
  return {
    message: trimmed || t("extraction.errors.fallback.message"),
    suggestion: t("extraction.errors.fallback.suggestion"),
  };
}
